import React, { useContext, useState } from "react";
import {
  Alert,
  Box,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslations, useForm } from "hooks";
import { getTranslation } from "common";
import { noTokenApi, organizationsApi } from "api";
import { Validation, isRequired } from "hooks/useForm";
import { AuthContext } from "contexts";
import { AuthSidebar } from "components";
import { Step } from "components/LandingSidebar";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

const STATIC_LANDING_REGISTER_USER_KEY = "flowenti/registerUser";

const getStaticLandingRegisterUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STATIC_LANDING_REGISTER_USER_KEY) || "null");
  } catch {
    return null;
  }
};

const Organization = () => {
  const translations = useTranslations();
  const lang = navigator.language.split("-")[0] || "en";
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const {
    state: { registerUser: user },
    actions: { setUser },
  } = useContext(AuthContext);
  // Static HTML landing pages cannot write into AuthContext before navigating
  // to this React route. This fallback is intentionally limited to that flow.
  const staticLandingRegisterUser = user ? null : getStaticLandingRegisterUser();
  const thankYouUrl = `/thank-you-${lang}.html${user?.authenticationtype != null && user.authenticationtype !== 0 ? "?source=google" : ""}`;
  const staticLandingThankYouUrl = `/thank-you-${lang}.html${staticLandingRegisterUser?.authenticationtype != null && staticLandingRegisterUser.authenticationtype !== 0 ? "?source=google" : ""}`;
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const organizationInitial: string | undefined = getTranslation(
    translations,
    "register.label.edit_myOrganization"
  );
  const validations: Validation[] = [
    ({ organization }) =>
      isRequired(organization) || {
        organization: getTranslation(translations, "register.fieldrequired"),
      },
  ];
  const { values, changeHandler, errors, touched, isValid } = useForm(
    {
      organization: !organizationInitial ? "" : organizationInitial,
      emails: [],
    },
    validations
  );

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      setLoading(true);
      const result = await organizationsApi.post(
        "",
        {
          name: values.organization,
        },
        { params: { create_template: true } }
      );

      const organizationId = result.data.id;

      if (values.emails.length > 0) {
        await noTokenApi.post(
          `/users/${user?.userId}/organizations/${organizationId}/inviteusers`,
          {
            email: values.emails,
          }
        );
      }
      setEmailSent(true);
      if (user?.authenticationtype != null && user.authenticationtype !== 0) {
        setUser({
          jwt: user.jwt || "",
          userId: user.userId || -1,
          email: user.email || "",
          username: user.username || "",
          firstname: user.firstname || "",
          lastname: user.lastname || "",
          picture: user.picture || "",
          linkprofile: user.linkprofile || "",
          subscriptiontype: user.subscriptiontype || 0,
          i18n: user.i18n || "en",
          dob: user.dob || null,
          authenticationtype: user.authenticationtype,
        });
      }
      try {
        sessionStorage.setItem("flowenti/openai-registration-pending", "true");
      } catch (error) {
        console.error("[Tracking] OpenAI registration tracking unavailable", error);
      }
      window.location.href = thankYouUrl;
    } catch {
      setLoading(false);
    } finally {
    }
  };

  // Duplicate submit path used only when the user started Google registration
  // from a static HTML landing page, outside the React AuthContext lifecycle.
  const handleStaticLandingSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      setLoading(true);
      const result = await organizationsApi.post(
        "",
        {
          name: values.organization,
        },
        { params: { create_template: true } }
      );

      const organizationId = result.data.id;

      if (values.emails.length > 0) {
        await noTokenApi.post(
          `/users/${staticLandingRegisterUser?.userId}/organizations/${organizationId}/inviteusers`,
          {
            email: values.emails,
          }
        );
      }
      setEmailSent(true);
      if (staticLandingRegisterUser?.authenticationtype != null && staticLandingRegisterUser.authenticationtype !== 0) {
        setUser({
          jwt: staticLandingRegisterUser.jwt || "",
          userId: staticLandingRegisterUser.userId || -1,
          email: staticLandingRegisterUser.email || "",
          username: staticLandingRegisterUser.username || "",
          firstname: staticLandingRegisterUser.firstname || "",
          lastname: staticLandingRegisterUser.lastname || "",
          picture: staticLandingRegisterUser.picture || "",
          linkprofile: staticLandingRegisterUser.linkprofile || "",
          subscriptiontype: staticLandingRegisterUser.subscriptiontype || 0,
          i18n: staticLandingRegisterUser.i18n || "en",
          dob: staticLandingRegisterUser.dob || null,
          authenticationtype: staticLandingRegisterUser.authenticationtype,
        });
        sessionStorage.removeItem(STATIC_LANDING_REGISTER_USER_KEY);
      }
      try {
        sessionStorage.setItem("flowenti/openai-registration-pending", "true");
      } catch (error) {
        console.error("[Tracking] OpenAI registration tracking unavailable", error);
      }
      window.location.href = staticLandingThankYouUrl;
    } catch {
      setLoading(false);
    } finally {
    }
  };

  const steps: Step[] = [
    {
      number: 1,
      title: getTranslation(translations, "setup.organization_title1"),
      description: getTranslation(translations, "setup.organization_description1")
    },
    {
      number: 2,
      title: getTranslation(translations, "setup.organization_title2"),
      description: getTranslation(translations, "setup.organization_description2")
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        bgcolor: "background.default",
        flexDirection: { xs: "column-reverse", md: "row" },
      }}
    >
      <AuthSidebar
        title={getTranslation(translations, "set.up.organization_title")}
        steps={steps}
      />

      {/* Right Section - Organization Form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, md: 6 },
          bgcolor: "background.paper",
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="form"
            sx={{
              width: '100%',
              maxWidth: 480,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: "1.75rem",
                fontWeight: 600,
                color: "text.primary",
                mb: 2,
              }}
            >
              {getTranslation(
                translations,
                "register.label.organization_name_insert"
              )}
            </Typography>

            <Typography
              sx={{
                mb: 4,
                color: "text.secondary",
                lineHeight: 1.6,
                fontSize: "0.95rem",
              }}
            >
              {getTranslation(
                translations,
                "register.label.organization_name_describe"
              )}
            </Typography>

            <TextField
              size="medium"
              fullWidth
              error={Boolean(touched.organization && errors.organization)}
              helperText={touched.organization && errors.organization}
              onChange={(e) =>
                changeHandler("organization", e.target.value)
              }
              value={values.organization}
              label={getTranslation(
                translations,
                "register.label.organization_set_name"
              )}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: "background.paper",
                }
              }}
            />

            {!emailSent && (
              <FlowentiButton
                id="organization-button"
                variant="contained"
                fullWidth
                type="submit"
                size="large"
                disabled={!isValid || loading}
                onClick={staticLandingRegisterUser ? handleStaticLandingSubmit : handleSubmit}
              >
                {loading ? getTranslation(translations, "generic.loading") : getTranslation(translations, "register.label.button_next")}
              </FlowentiButton>
            )}

            {emailSent && (
              <Box sx={{
                display: "flex",
                alignItems: "center",
                // justifyContent: "center",
              }}>
                <Alert
                  severity="success"
                  sx={{
                    borderRadius: 1.5,
                    "& .MuiAlert-message": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                >
                  {getTranslation(translations, "generic.check_your_email")}
                </Alert>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Organization;
